import React, { Suspense, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import {
  Appointment,
  User,
  Service,
  AddOn,
  QuestionnaireSubmission,
  QuestionnaireTemplate,
  MedicalCertificate,
  CertificateResultRow,
  CertificateResultOutcome,
  CertificateSigner,
  Venue,
  SystemConfig,
  Question,
  HealthAssessment
} from '@/types';
import { HealthAssessmentModal } from './HealthAssessmentModal';
import { TechnicianQuestionnaireModal } from './TechnicianQuestionnaireModal';
import { HealthAssessmentView } from '@shared/components/HealthAssessmentView';
import { getPendingQuestionnaireTemplateIds } from '@/lib/questionnaireRequirements';
import { findSubmittedJobTitle } from '@/lib/submittedJobTitle';
import { getAppointmentServiceIds, getAppointmentServiceNames } from '@/lib/appointmentServices';
import {
  buildCertificateResults as buildCertificateResultRows,
  getExtraResultItems as listExtraResultItems,
  getIssueCertificateWarnings,
  hasDrugAndAlcoholBooked,
  isDrugAndAlcoholName
} from '@/lib/certificateResults';
import {
  StaffDataProvider,
  DataErrorBoundary,
  useAppointments,
  useAppointmentMutations,
  useUserMutations,
  type AppointmentListParams
} from '@features/admin/data';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { AppointmentDocumentsPanel } from '@shared/components/common/AppointmentDocumentsPanel';
import {
  Clock,
  CheckSquare,
  FileText,
  UserX,
  AlertCircle,
  X,
  Award,
  Stethoscope,
  TestTube,
  PenTool,
  Printer,
  Eraser,
  Loader2,
  ChevronRight,
  MapPin,
  Calendar,
  ScrollText,
  ClipboardCheck,
  MessageSquareQuote,
  Upload,
  Trash2
} from 'lucide-react';
import { api } from '@/services/api';
import { createId } from '@/lib/ids';
import { formatUkDateTime, formatUkDate } from '@/lib/dates';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { buildMapById } from '@/lib/collections';
import {
  buildCertificateFileName,
  buildCertificateHtml,
  canPrintCertificate,
  certificateHasRestrictionOutcome,
  openCertificatePrintWindow,
  resolveCertificateClientName,
  resolveCertificateRestrictions
} from '@/lib/certificateRenderer';

const CERTIFICATE_OUTCOMES = [
  {
    id: 'fit',
    label: 'Fit for Duties',
    bg: 'bg-green-50',
    border: 'border-green-500',
    text: 'text-green-700',
    hover: 'hover:bg-green-50'
  },
  {
    id: 'fit_restrictions',
    label: 'Fit with Restrictions',
    bg: 'bg-yellow-50',
    border: 'border-yellow-500',
    text: 'text-yellow-700',
    hover: 'hover:bg-yellow-50'
  },
  {
    id: 'referred',
    label: 'Referral Required',
    bg: 'bg-blue-50',
    border: 'border-blue-500',
    text: 'text-blue-700',
    hover: 'hover:bg-blue-50'
  },
  {
    id: 'unfit',
    label: 'Unfit for Work',
    bg: 'bg-red-50',
    border: 'border-red-500',
    text: 'text-red-700',
    hover: 'hover:bg-red-50'
  }
] as const;

interface TechnicianViewProps {
  currentUser: User;
  services: Service[];
  addOns?: AddOn[];
  venues: Venue[];
  questionnaires: QuestionnaireTemplate[];
  submissions: QuestionnaireSubmission[];
  systemConfig?: SystemConfig;
  onUpdateUser?: (user: User) => void;
  onIssueCertificate?: (cert: MedicalCertificate) => Promise<{ success: boolean; error?: string }>;
  onAmendCertificate?: (
    certificateId: string,
    fields: Partial<
      Pick<
        MedicalCertificate,
        | 'validUntil'
        | 'outcome'
        | 'restrictions'
        | 'advice'
        | 'technicianName'
        | 'technicianSignature'
        | 'jobTitle'
      >
      // null clears the stored drug result ("Not Recorded").
    > & { drugTestOutcome?: MedicalCertificate['drugTestOutcome'] | null }
  ) => Promise<{ success: boolean; error?: string; certificate?: MedicalCertificate }>;
  certificates?: MedicalCertificate[];
}

const SignatureCanvas = ({
  onSave,
  onCancel,
  initialDataUrl
}: {
  onSave: (dataUrl: string) => void;
  onCancel: () => void;
  initialDataUrl?: string;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#000';
      }
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !initialDataUrl) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
      const drawWidth = img.width * scale;
      const drawHeight = img.height * scale;
      const dx = (canvas.width - drawWidth) / 2;
      const dy = (canvas.height - drawHeight) / 2;
      ctx.drawImage(img, dx, dy, drawWidth, drawHeight);
    };
    img.src = initialDataUrl;
  }, [initialDataUrl]);

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x =
      'touches' in e
        ? e.touches[0].clientX - rect.left
        : (e as React.MouseEvent).nativeEvent.offsetX;
    const y =
      'touches' in e
        ? e.touches[0].clientY - rect.top
        : (e as React.MouseEvent).nativeEvent.offsetY;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x =
      'touches' in e
        ? e.touches[0].clientX - rect.left
        : (e as React.MouseEvent).nativeEvent.offsetX;
    const y =
      'touches' in e
        ? e.touches[0].clientY - rect.top
        : (e as React.MouseEvent).nativeEvent.offsetY;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="bg-white border-2 border-dashed border-slate-300 rounded-xl mb-4 relative h-64 overflow-hidden shadow-inner">
        <canvas
          ref={canvasRef}
          className="w-full h-full cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={() => setIsDrawing(false)}
          onMouseLeave={() => setIsDrawing(false)}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={() => setIsDrawing(false)}
        />
        <div className="absolute top-2 right-2">
          <button
            onClick={clearCanvas}
            className="bg-white/80 p-2 rounded-full hover:bg-white shadow-sm text-slate-500"
          >
            <Eraser className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded font-bold"
        >
          Cancel
        </button>
        <button
          onClick={() => canvasRef.current && onSave(canvasRef.current.toDataURL())}
          className="bg-slate-900 text-white px-6 py-2 rounded-xl font-black hover:bg-slate-800 transition-all flex items-center gap-2 uppercase tracking-wide text-xs"
        >
          Save Signature
        </button>
      </div>
    </div>
  );
};

export const TechnicianView: React.FC<TechnicianViewProps> = (props) => {
  return (
    <StaffDataProvider>
      <TechnicianViewInner {...props} />
    </StaffDataProvider>
  );
};

const TechnicianViewInner: React.FC<TechnicianViewProps> = ({
  currentUser,
  services,
  addOns = [],
  venues,
  questionnaires,
  submissions,
  systemConfig,
  onUpdateUser,
  onIssueCertificate,
  onAmendCertificate,
  certificates = []
}) => {
  const appointmentMutations = useAppointmentMutations();
  const userMutations = useUserMutations();
  const [selectedVenueFilter, setSelectedVenueFilter] = useState<string>('all');
  const [dateRangeFilter, setDateRangeFilter] = useState<'past' | 'today' | 'tomorrow' | 'week'>(
    'today'
  );
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'booked' | 'completed' | 'noshow' | 'cancelled'
  >('all');
  const [serviceFilter, setServiceFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [noShowConfirmId, setNoShowConfirmId] = useState<string | null>(null);
  const [notifyOptions, setNotifyOptions] = useState({ email: true, sms: true });
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isIssuing, setIsIssuing] = useState(false);
  const [issuingCertApp, setIssuingCertApp] = useState<Appointment | null>(null);
  const [amendingCertId, setAmendingCertId] = useState<string | null>(null);
  const [issuedCertificate, setIssuedCertificate] = useState<{
    appointment: Appointment;
    certificate: MedicalCertificate;
  } | null>(null);
  const createDefaultCertData = (): Partial<MedicalCertificate> => ({
    outcome: 'fit',
    advice: '',
    restrictions: ''
  });
  const [certData, setCertData] = useState<Partial<MedicalCertificate>>(createDefaultCertData);
  // Who signs the certificate. Shared devices mean the logged-in user isn't
  // always the technician who saw the patient, so the signer is selectable.
  // '' in amend mode means "keep the technician already on the certificate".
  const [signers, setSigners] = useState<CertificateSigner[]>([]);
  const [selectedSignerId, setSelectedSignerId] = useState<string>('');
  // Outcomes for services beyond the first and non-D&A add-ons, keyed
  // `${kind}:${id}`. The first service mirrors certData.outcome and the D&A
  // add-on mirrors certData.drugTestOutcome (existing pickers).
  const [extraResults, setExtraResults] = useState<Record<string, CertificateResultOutcome>>({});

  // One row per booked service and add-on test — persisted on the certificate
  const buildCertificateResults = (app: Appointment): CertificateResultRow[] =>
    buildCertificateResultRows({
      appointment: app,
      servicesById,
      addOnsById,
      primaryOutcome: certData.outcome || 'fit',
      drugTestOutcome: certData.drugTestOutcome,
      extraResults
    });

  const getExtraResultItems = (app: Appointment) =>
    listExtraResultItems({ appointment: app, servicesById, addOnsById });

  const getPrimaryServiceName = (app: Appointment): string => {
    const primary = getAppointmentServiceIds(app)
      .map((id) => servicesById.get(id))
      .find((service): service is Service =>
        Boolean(service && !isDrugAndAlcoholName(service.name))
      );
    return primary?.name || 'the main service';
  };
  const isAmendingMode = Boolean(amendingCertId);
  const [showBriefingOverlay, setShowBriefingOverlay] = useState(false);
  const [localComments, setLocalComments] = useState<Record<string, Record<string, string>>>({});
  const [uploadingPaperFor, setUploadingPaperFor] = useState<string | null>(null);
  const [fillingQuestionnaireTemplateId, setFillingQuestionnaireTemplateId] = useState<
    string | null
  >(null);
  const [isHealthAssessmentModalOpen, setIsHealthAssessmentModalOpen] = useState(false);
  const [localHealthAssessments, setLocalHealthAssessments] = useState<
    Record<string, HealthAssessment>
  >({});

  const getHealthAssessment = (app: Appointment): HealthAssessment | undefined =>
    localHealthAssessments[app.id] ?? app.healthAssessment;

  const [localSubmissions, setLocalSubmissions] = useState<QuestionnaireSubmission[]>([]);
  const [removedSubmissionIds, setRemovedSubmissionIds] = useState<Set<string>>(new Set());
  const [removingScanId, setRemovingScanId] = useState<string | null>(null);
  const servicesById = useMemo(() => buildMapById(services), [services]);
  const addOnsById = useMemo(() => buildMapById(addOns), [addOns]);
  const venuesById = useMemo(() => buildMapById(venues), [venues]);
  const questionnairesById = useMemo(() => buildMapById(questionnaires), [questionnaires]);
  const certificatesByAppointmentId = useMemo(() => {
    const map = new Map<string, MedicalCertificate>();
    certificates.forEach((c) => {
      if (c?.appointmentId) map.set(c.appointmentId, c);
    });
    return map;
  }, [certificates]);

  const [pageItems, setPageItems] = useState<Appointment[]>([]);
  const [pageTotalCount, setPageTotalCount] = useState(0);

  // Refresh the signer list whenever the certificate panel opens, so a
  // signature saved moments ago on another device is picked up.
  useEffect(() => {
    if (!issuingCertApp) return;
    let cancelled = false;
    api
      .getCertificateSigners()
      .then(({ signers: loaded }) => {
        if (!cancelled) setSigners(loaded);
      })
      .catch(() => {
        // Dropdown falls back to the logged-in technician only.
      });
    return () => {
      cancelled = true;
    };
  }, [issuingCertApp]);

  useEffect(() => {
    setPage(1);
  }, [selectedVenueFilter, dateRangeFilter, statusFilter, serviceFilter, searchTerm, pageSize]);

  const deferredSearchTerm = useDeferredValue(searchTerm);

  // Translate the date-range filter into server-side dateFrom/dateTo (YYYY-MM-DD).
  // Use the technician's LOCAL date — Date.toISOString() returns UTC, which would
  // shift the day boundary and show yesterday's bookings late at night, etc.
  const { dateFrom, dateTo } = useMemo<{ dateFrom?: string; dateTo: string }>(() => {
    const toLocalIso = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const today = new Date();
    if (dateRangeFilter === 'past') {
      // Everything before today, unbounded — technicians need to review
      // earlier visits (e.g. prior BP readings) at any distance back.
      return { dateFrom: undefined, dateTo: toLocalIso(new Date(Date.now() - 86400000)) };
    }
    if (dateRangeFilter === 'today') {
      const iso = toLocalIso(today);
      return { dateFrom: iso, dateTo: iso };
    }
    if (dateRangeFilter === 'tomorrow') {
      const iso = toLocalIso(new Date(Date.now() + 86400000));
      return { dateFrom: iso, dateTo: iso };
    }
    return {
      dateFrom: toLocalIso(today),
      dateTo: toLocalIso(new Date(Date.now() + 7 * 86400000))
    };
  }, [dateRangeFilter]);

  // Client-side venue restriction: when the technician has exactly one assigned venue
  // we narrow the server query. Multi-venue restriction is enforced server-side.
  const venueIdParam =
    selectedVenueFilter !== 'all'
      ? selectedVenueFilter
      : currentUser.assignedVenueIds?.length === 1
        ? currentUser.assignedVenueIds[0]
        : undefined;

  const totalFiltered = pageTotalCount;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const pageStart = (page - 1) * pageSize;
  const pageEnd = pageStart + pageSize;
  const pageRangeStart = totalFiltered === 0 ? 0 : pageStart + 1;
  const pageRangeEnd = Math.min(pageEnd, totalFiltered);
  const isStatusFilter = (
    value: string
  ): value is 'all' | 'booked' | 'completed' | 'noshow' | 'cancelled' => {
    return ['all', 'booked', 'completed', 'noshow', 'cancelled'].includes(value);
  };

  const closeRecordModal = () => {
    setIssuingCertApp(null);
    setAmendingCertId(null);
    setShowBriefingOverlay(false);
    setCertData(createDefaultCertData());
    setLocalComments({});
    setFillingQuestionnaireTemplateId(null);
  };

  const handleSaveComment = async (submissionId: string, questionId: string, value: string) => {
    const updated = {
      ...(localComments[submissionId] || {}),
      [questionId]: value
    };
    setLocalComments((prev) => ({ ...prev, [submissionId]: updated }));
    try {
      await api.saveTechnicianComments(submissionId, updated);
    } catch {
      // silent — comment preserved locally even if save fails
    }
  };

  const handlePaperUpload = async (app: Appointment, file: File) => {
    setUploadingPaperFor(app.id);
    try {
      const submission = await api.uploadPaperQuestionnaire(file, app.id, app.customerEmail);
      setLocalSubmissions((prev) => [...prev, submission]);
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to upload questionnaire.'));
    } finally {
      setUploadingPaperFor(null);
    }
  };

  const handleRemovePaperScan = async (submission: QuestionnaireSubmission) => {
    const confirmed = await showConfirmModal(
      'Remove this uploaded paper questionnaire? The scan will be deleted and the questionnaire will show as pending again.',
      { title: 'Remove Paper Questionnaire', confirmText: 'Remove', isDestructive: true }
    );
    if (!confirmed) return;
    setRemovingScanId(submission.id);
    try {
      await api.removePaperQuestionnaire(submission.id);
      setRemovedSubmissionIds((prev) => new Set(prev).add(submission.id));
      setLocalSubmissions((prev) => prev.filter((s) => s.id !== submission.id));
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to remove paper questionnaire.'));
    } finally {
      setRemovingScanId(null);
    }
  };

  const handleSubmitCertificate = async () => {
    if (!issuingCertApp) return;
    // Resolve who signs. In amend mode an empty selection keeps the
    // technician already on the certificate untouched.
    const keepExistingSigner = isAmendingMode && !selectedSignerId;
    const signingAsSelf =
      !keepExistingSigner && (!selectedSignerId || selectedSignerId === currentUser.id);
    const selectedSigner: CertificateSigner | undefined = signingAsSelf
      ? { id: currentUser.id, name: currentUser.name, signature: currentUser.signature }
      : signers.find((s) => s.id === selectedSignerId);
    if (signingAsSelf && !currentUser.signature) {
      setIsSignatureModalOpen(true);
      return;
    }
    if (!keepExistingSigner && !selectedSigner?.signature) {
      showAlertModal(
        'The selected technician has no saved signature. They need to save one from their own login before they can sign certificates.'
      );
      return;
    }

    if (!isAmendingMode) {
      const warnings = getIssueCertificateWarnings({
        pendingTemplateNames: getPendingTemplates(issuingCertApp).map((t) => t.name),
        submissionCount: getSubmissions(issuingCertApp.id).length,
        drugAndAlcoholBooked: hasDrugAndAlcoholBooked({
          appointment: issuingCertApp,
          servicesById,
          addOnsById
        }),
        drugTestOutcome: certData.drugTestOutcome
      });
      if (warnings.length > 0) {
        const confirmed = await showConfirmModal(
          `${warnings.join('\n\n')}\n\nAre you sure you want to issue the certificate?`,
          {
            title: 'Check Before Issuing',
            confirmText: 'Issue Anyway',
            isDestructive: false
          }
        );
        if (!confirmed) return;
      }
    }

    setIsIssuing(true);

    // Restrictions only belong on a certificate that concludes "Fit with
    // Restrictions" (on the conclusion itself or on one of the per-test rows).
    // Concluding otherwise drops the text instead of storing restrictions the
    // conclusion contradicts — including when an amend changes the conclusion
    // back to Fit.
    const carriesRestrictions = certificateHasRestrictionOutcome({
      outcome: certData.outcome || 'fit',
      results: buildCertificateResults(issuingCertApp)
    });

    if (isAmendingMode && amendingCertId) {
      try {
        if (onAmendCertificate) {
          const res = await onAmendCertificate(amendingCertId, {
            outcome: certData.outcome,
            // Explicit null, not undefined: switching back to "Not Recorded"
            // has to clear the stored result, otherwise the certificate keeps
            // printing the result the technician just removed.
            drugTestOutcome: certData.drugTestOutcome || null,
            advice: certData.advice,
            restrictions: carriesRestrictions ? certData.restrictions : '',
            jobTitle: (certData.jobTitle || '').trim(),
            ...(keepExistingSigner || !selectedSigner
              ? {}
              : {
                  technicianName: selectedSigner.name,
                  technicianSignature: selectedSigner.signature || undefined
                })
          });
          if (res.success) {
            closeRecordModal();
            showAlertModal('Certificate amended.');
          } else if (res.error) {
            showAlertModal(res.error);
          }
        }
      } catch (err: unknown) {
        showAlertModal(normalizeErrorMessage(err, 'Error amending certificate.'));
      } finally {
        setIsIssuing(false);
      }
      return;
    }

    const today = new Date();
    const expiry = new Date();
    expiry.setFullYear(today.getFullYear() + 3);
    const certId = createId().toUpperCase();
    const newCertificate: MedicalCertificate = {
      id: certId,
      appointmentId: issuingCertApp.id,
      issuedAt: today.toISOString(),
      validUntil: expiry.toISOString(),
      technicianName: selectedSigner?.name ?? currentUser.name,
      technicianSignature: selectedSigner?.signature || currentUser.signature,
      outcome: certData.outcome || 'fit',
      drugTestOutcome: certData.drugTestOutcome || undefined,
      results: buildCertificateResults(issuingCertApp),
      advice: certData.advice,
      // Combine the restriction clauses the technician selected in the Health Assessment
      // ("Fit with Restrictions") with any additional free text typed on the certificate
      // panel, so the selection actually reaches the generated certificate.
      restrictions: carriesRestrictions
        ? resolveCertificateRestrictions(getHealthAssessment(issuingCertApp), certData.restrictions)
        : undefined,
      jobTitle: (certData.jobTitle || '').trim() || undefined,
      templateId: 'default'
    };
    try {
      if (onIssueCertificate) {
        const res = await onIssueCertificate(newCertificate);
        if (res.success) {
          const certifiedApp = issuingCertApp;
          closeRecordModal();
          setIssuedCertificate({ appointment: certifiedApp, certificate: newCertificate });
        }
      }
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Error issuing certificate.'));
    } finally {
      setIsIssuing(false);
    }
  };

  const openCertificateRecord = (app: Appointment) => {
    setShowBriefingOverlay(false);
    setAmendingCertId(null);
    setCertData({
      ...createDefaultCertData(),
      // Public/company bookings often arrive without a job title, but the
      // health questionnaire asks for it — pre-fill from the patient's answer
      // so the technician only confirms it.
      jobTitle:
        app.customerJobTitle || findSubmittedJobTitle(getSubmissions(app.id), questionnairesById)
    });
    setExtraResults({});
    setSelectedSignerId(currentUser.id);
    setIssuingCertApp(app);
    const existingComments: Record<string, Record<string, string>> = {};
    submissions
      .filter((s) => s.appointmentId === app.id)
      .forEach((s) => {
        if (s.technicianComments && Object.keys(s.technicianComments).length > 0) {
          existingComments[s.id] = { ...s.technicianComments };
        }
      });
    setLocalComments(existingComments);
  };

  const openAmendRecord = (app: Appointment) => {
    const cert = certificatesByAppointmentId.get(app.id);
    if (!cert) {
      showAlertModal('Certificate not loaded for this appointment.');
      return;
    }
    if (cert.status === 'revoked') {
      showAlertModal(
        'This certificate has been revoked and cannot be amended. Issue a new certificate instead.'
      );
      return;
    }
    setShowBriefingOverlay(false);
    setAmendingCertId(cert.id);
    setCertData({
      outcome: cert.outcome,
      drugTestOutcome: cert.drugTestOutcome,
      advice: cert.advice || '',
      restrictions: cert.restrictions || '',
      jobTitle:
        cert.jobTitle ||
        app.customerJobTitle ||
        findSubmittedJobTitle(getSubmissions(app.id), questionnairesById)
    });
    setExtraResults(
      Object.fromEntries((cert.results || []).map((row) => [`${row.kind}:${row.id}`, row.outcome]))
    );
    setSelectedSignerId('');
    setIssuingCertApp(app);
    const existingComments: Record<string, Record<string, string>> = {};
    submissions
      .filter((s) => s.appointmentId === app.id)
      .forEach((s) => {
        if (s.technicianComments && Object.keys(s.technicianComments).length > 0) {
          existingComments[s.id] = { ...s.technicianComments };
        }
      });
    setLocalComments(existingComments);
  };

  const handlePrintCertificate = (app: Appointment, certificate?: MedicalCertificate) => {
    const cert = certificate ?? certificatesByAppointmentId.get(app.id);
    if (!cert || !systemConfig || !canPrintCertificate(cert)) return;
    const html = buildCertificateHtml({
      template: systemConfig.templates.certificateTemplate.body,
      systemConfig,
      appointment: app,
      certificate: cert,
      clientName: resolveCertificateClientName(app)
    });
    openCertificatePrintWindow(
      html,
      buildCertificateFileName({ appointment: app, certificate: cert })
    );
  };

  // Helper to find questionnaire answers
  const getSubmissions = (appId: string) =>
    [...submissions, ...localSubmissions]
      .filter((s) => s.appointmentId === appId && !removedSubmissionIds.has(s.id))
      .sort((a, b) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime());
  const getTemplate = (templateId?: string) =>
    templateId ? questionnairesById.get(templateId) : undefined;
  // Questionnaire templates still outstanding for an appointment — these can be
  // completed digitally with the candidate at the appointment (e.g. the D&A
  // consent) instead of uploading a paper scan.
  const getPendingTemplates = (app: Appointment): QuestionnaireTemplate[] =>
    getPendingQuestionnaireTemplateIds(app, getSubmissions(app.id), servicesById, addOnsById)
      .map((templateId) => questionnairesById.get(templateId))
      .filter((t): t is QuestionnaireTemplate => Boolean(t));
  const QUESTION_COMMENT_SUFFIXES = new Set([
    'details',
    'detail',
    'comment',
    'comments',
    'commenttext',
    'detailstext',
    'note',
    'notes',
    'additionalinfo',
    'additionaldetails'
  ]);
  const getQuestionScopedSuffix = (key: string, questionId: string): string | null => {
    const normalizedKey = String(key || '')
      .trim()
      .toLowerCase();
    const normalizedQuestionId = String(questionId || '')
      .trim()
      .toLowerCase();
    if (
      !normalizedKey ||
      !normalizedQuestionId ||
      !normalizedKey.startsWith(normalizedQuestionId)
    ) {
      return null;
    }

    let suffix = normalizedKey.slice(normalizedQuestionId.length);
    if (!suffix) return '';
    if (suffix[0] === '_' || suffix[0] === '.' || suffix[0] === '-') {
      suffix = suffix.slice(1);
    }
    return suffix || null;
  };
  const normalizeAnswerValue = (value: unknown): string => {
    if (value == null) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'object') {
      const nested = value as Record<string, unknown>;
      const preferredKeys = ['value', 'answer', 'response', 'selected', 'text'];
      for (const key of preferredKeys) {
        const candidate = nested[key];
        if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
      }
      return JSON.stringify(value);
    }
    return String(value);
  };
  const extractQuestionComment = (
    answers: Record<string, unknown>,
    questionId: string,
    rawAnswer: unknown
  ): string => {
    if (rawAnswer && typeof rawAnswer === 'object') {
      const nested = rawAnswer as Record<string, unknown>;
      const nestedKeys = [
        'details',
        'detail',
        'comment',
        'comments',
        'commentText',
        'detailsText',
        'note',
        'notes',
        'additionalInfo'
      ];
      for (const key of nestedKeys) {
        const nestedText = normalizeAnswerValue(nested[key]);
        if (nestedText) return nestedText;
      }
    }

    const preferredKeys = [
      `${questionId}_details`,
      `${questionId}_detail`,
      `${questionId}_comment`,
      `${questionId}_comments`,
      `${questionId}Details`,
      `${questionId}Detail`,
      `${questionId}Comment`,
      `${questionId}Comments`,
      `${questionId}_comment_text`,
      `${questionId}_details_text`,
      `${questionId}CommentText`,
      `${questionId}DetailsText`,
      `${questionId}_note`,
      `${questionId}_notes`,
      `${questionId}Note`,
      `${questionId}Notes`,
      `${questionId}_additional_info`,
      `${questionId}AdditionalInfo`
    ];
    const dynamicKeys = Object.keys(answers).filter((key) => {
      const suffix = getQuestionScopedSuffix(key, questionId);
      return Boolean(suffix && QUESTION_COMMENT_SUFFIXES.has(suffix));
    });
    const candidates = Array.from(new Set([...preferredKeys, ...dynamicKeys]));

    for (const key of candidates) {
      const text = normalizeAnswerValue(answers[key]);
      if (text) return text;
    }

    return '';
  };
  const isQuestionScopedKey = (key: string, questionId: string) =>
    String(key || '')
      .trim()
      .toLowerCase() ===
      String(questionId || '')
        .trim()
        .toLowerCase() || getQuestionScopedSuffix(key, questionId) !== null;
  const getUnmatchedAnswerEntries = (
    answers: Record<string, unknown>,
    questions: Question[]
  ): Array<[string, unknown]> => {
    const questionIds = new Set(questions.map((q) => q.id));
    return Object.entries(answers).filter(([key]) => {
      if (questionIds.has(key)) return false;
      return !questions.some((q) => isQuestionScopedKey(key, q.id));
    });
  };

  return (
    <div className="w-full px-4 sm:px-8 lg:px-12 xl:px-16 py-8 animate-fade-in">
      {/* Signature Modal */}
      {isSignatureModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-scale-up overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center bg-slate-50">
              <h3 className="font-black text-slate-800 flex items-center gap-2 uppercase tracking-tight text-sm">
                <PenTool className="w-4 h-4 text-teal-600" /> Digital Signature Setup
              </h3>
              <button onClick={() => setIsSignatureModalOpen(false)}>
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-xs text-slate-500 mb-6 leading-relaxed">
                This signature will be appended to all clinical certificates you issue. Please
                ensure it is legible.
              </p>
              {currentUser.signature && (
                <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
                    Current Signature
                  </p>
                  <div className="bg-white border border-dashed border-slate-200 rounded-lg p-3 flex items-center justify-center">
                    <img
                      src={currentUser.signature}
                      alt="Current signature"
                      className="max-h-24 w-auto object-contain"
                    />
                  </div>
                </div>
              )}
              <SignatureCanvas
                onSave={async (url) => {
                  try {
                    await userMutations.updateOwnSignature(currentUser.id, url);
                    onUpdateUser?.({ ...currentUser, signature: url });
                    showAlertModal('Signature saved.');
                    setIsSignatureModalOpen(false);
                  } catch (err: unknown) {
                    showAlertModal(normalizeErrorMessage(err, 'Failed to update signature.'));
                  }
                }}
                onCancel={() => setIsSignatureModalOpen(false)}
                initialDataUrl={currentUser.signature}
              />
            </div>
          </div>
        </div>
      )}

      {/* Staff Briefing Overlay */}
      {showBriefingOverlay && issuingCertApp && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg animate-scale-up overflow-hidden border-4 border-amber-100">
            <div className="p-6 border-b bg-amber-50 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="bg-amber-200 p-2 rounded-xl text-amber-700 shadow-inner">
                  <ScrollText className="w-5 h-5" />
                </div>
                <h3 className="font-black text-lg text-amber-900 uppercase tracking-tight">
                  Staff Briefing Notes
                </h3>
              </div>
              <button
                onClick={() => setShowBriefingOverlay(false)}
                className="bg-white p-1.5 rounded-full text-amber-400 hover:text-amber-600 shadow-sm"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-8 space-y-6">
              {issuingCertApp.staffPrivateNote ? (
                <div className="space-y-2">
                  <p className="text-[10px] font-black text-amber-600 uppercase tracking-[0.2em]">
                    Booking Office Instructions:
                  </p>
                  <div className="bg-amber-50/50 p-4 rounded-2xl text-sm text-slate-700 italic border-l-4 border-amber-400 font-medium leading-relaxed">
                    "{issuingCertApp.staffPrivateNote}"
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic">
                  No admin notes provided for this booking.
                </p>
              )}

              {issuingCertApp.technicianNotes && (
                <div className="space-y-2 pt-4 border-t border-slate-100">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                    Previous Clinical History:
                  </p>
                  <div className="bg-slate-50 p-4 rounded-2xl text-sm text-slate-600 italic border-l-4 border-slate-300 font-medium leading-relaxed">
                    "{issuingCertApp.technicianNotes}"
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 bg-slate-50 flex justify-center">
              <button
                onClick={() => setShowBriefingOverlay(false)}
                className="w-full bg-slate-900 text-white py-3 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/10"
              >
                Return to Assessment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Issuance Modal */}
      {issuingCertApp && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[90] p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[95vh] flex flex-col animate-scale-up border border-slate-200 overflow-hidden">
            <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="bg-teal-100 p-2.5 rounded-2xl text-teal-600 shadow-sm">
                  <Stethoscope className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-black text-xl text-slate-800 uppercase tracking-tight">
                    {isAmendingMode ? 'Amend Certificate' : 'Clinical Assessment Record'}
                  </h3>
                  <div className="flex items-center gap-3">
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                      PATIENT: {issuingCertApp.customerName} | Ref: {issuingCertApp.id}
                    </p>
                    {issuingCertApp.staffPrivateNote && (
                      <button
                        onClick={() => setShowBriefingOverlay(true)}
                        className="bg-amber-500 text-white text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest animate-pulse hover:bg-amber-600 transition-colors shadow-sm flex items-center gap-1"
                      >
                        <MessageSquareQuote className="w-3 h-3" /> Staff Note Available
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowBriefingOverlay(true)}
                  title="View Staff Notes"
                  className="hidden sm:flex items-center gap-2 text-[10px] font-black uppercase tracking-widest bg-white text-amber-600 border-2 border-amber-100 px-4 py-2 rounded-2xl hover:bg-amber-50 transition-all shadow-sm"
                >
                  <ScrollText className="w-4 h-4" /> Briefing
                </button>
                <button
                  onClick={closeRecordModal}
                  disabled={isIssuing}
                  className="text-slate-400 hover:text-slate-700 bg-white p-2 rounded-full shadow-sm"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-8 overflow-y-auto flex-1 custom-scrollbar space-y-10">
              {/* Quick Briefing Button (Inline for Mobile/Ease of use) */}
              {(issuingCertApp.staffPrivateNote || issuingCertApp.technicianNotes) && (
                <button
                  onClick={() => setShowBriefingOverlay(true)}
                  className="w-full bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 flex items-center justify-between group hover:bg-amber-100 transition-all text-left"
                >
                  <div className="flex items-center gap-4">
                    <div className="bg-white p-2 rounded-xl text-amber-600 shadow-sm group-hover:scale-110 transition-transform">
                      <ScrollText className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-black text-amber-800 text-[10px] uppercase tracking-[0.2em]">
                        Patient Briefing Required
                      </p>
                      <p className="text-xs text-amber-600 font-medium">
                        Click to view private staff notes and clinical history
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-amber-400" />
                </button>
              )}

              {issuingCertApp &&
                getHealthAssessment(issuingCertApp) &&
                certificatesByAppointmentId.has(issuingCertApp.id) && (
                  <HealthAssessmentView data={getHealthAssessment(issuingCertApp)!} />
                )}

              {/* Details of Candidate */}
              <div>
                <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                  <Stethoscope className="w-4 h-4 text-teal-600" /> Details of Candidate
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Full Name', value: issuingCertApp.customerName },
                    {
                      label: 'Date of Birth',
                      value: issuingCertApp.customerDob
                        ? formatUkDate(issuingCertApp.customerDob, '—')
                        : '—'
                    }
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">
                        {label}
                      </p>
                      <p className="text-xs font-semibold text-slate-700 leading-snug">{value}</p>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
                  {[
                    { label: 'Telephone', value: issuingCertApp.customerPhone || '—' },
                    { label: 'Employer', value: issuingCertApp.companyName || '—' },
                    {
                      label: 'Site Address',
                      value: (() => {
                        const v = venuesById.get(issuingCertApp.venueId);
                        if (!v) return '—';
                        return [v.address, v.city].filter(Boolean).join(', ');
                      })()
                    }
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">
                        {label}
                      </p>
                      <p className="text-xs font-semibold text-slate-700 leading-snug">{value}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1 block">
                    Job Title
                  </label>
                  <input
                    type="text"
                    className="w-full border-2 border-slate-100 rounded-xl p-3 text-xs font-semibold text-slate-700 focus:ring-4 focus:ring-teal-500/10 focus:border-teal-400 outline-none transition-all bg-slate-50"
                    placeholder="e.g. Forklift Operator"
                    value={certData.jobTitle || ''}
                    onChange={(e) => setCertData({ ...certData, jobTitle: e.target.value })}
                  />
                  <p className="mt-1 text-[10px] text-slate-500">
                    Appears on the certificate. Edit if the booked job title is missing or
                    incorrect.
                  </p>
                </div>
              </div>

              {/* Booked Services & Add-ons */}
              <div className="border-t border-slate-100 pt-8">
                <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                  <ClipboardCheck className="w-4 h-4 text-teal-600" /> Booked Services
                </h4>
                <div className="flex flex-wrap items-center gap-2">
                  {getAppointmentServiceIds(issuingCertApp).map((serviceId) => (
                    <span
                      key={serviceId}
                      className="bg-teal-50 text-teal-700 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider border border-teal-100"
                    >
                      {servicesById.get(serviceId)?.name || 'Service'}
                    </span>
                  ))}
                  {(issuingCertApp.addOnIds || [])
                    .map((id) => addOnsById.get(id))
                    .filter((a): a is AddOn => Boolean(a))
                    .map((addOn) => (
                      <span
                        key={addOn.id}
                        className="bg-purple-50 text-purple-700 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider border border-purple-100"
                      >
                        + {addOn.name}
                      </span>
                    ))}
                </div>
              </div>

              {/* Questionnaire Section */}
              <div className="border-t border-slate-100 pt-8">
                <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                  <ClipboardCheck className="w-4 h-4 text-teal-600" /> Health Questionnaire
                  Responses
                </h4>
                {(() => {
                  const appSubmissions = getSubmissions(issuingCertApp.id);
                  const service = servicesById.get(issuingCertApp.serviceId);
                  const pendingTemplates = getPendingTemplates(issuingCertApp);

                  const completeOnlineButtons = pendingTemplates.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => setFillingQuestionnaireTemplateId(template.id)}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all shadow-sm bg-slate-900 text-white hover:bg-slate-800"
                    >
                      <ClipboardCheck className="w-4 h-4" />
                      Complete “{template.name}” with Candidate
                    </button>
                  ));

                  if (appSubmissions.length === 0) {
                    const isUploading = uploadingPaperFor === issuingCertApp.id;
                    return (
                      <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-8 text-center">
                        <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500 font-medium italic mb-4">
                          No questionnaire responses found for this appointment.
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-3">
                          {completeOnlineButtons}
                          <label
                            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest cursor-pointer transition-all shadow-sm ${isUploading ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-teal-600 text-white hover:bg-teal-700'}`}
                          >
                            {isUploading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4" />
                            )}
                            {isUploading ? 'Uploading…' : 'Upload Paper Questionnaire'}
                            <input
                              type="file"
                              accept="image/*,application/pdf"
                              className="hidden"
                              disabled={isUploading}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handlePaperUpload(issuingCertApp, file);
                                e.target.value = '';
                              }}
                            />
                          </label>
                        </div>
                        <p className="mt-3 text-[10px] text-slate-400">
                          Complete digitally with the candidate, or upload a scanned paper form
                          (images or PDF, max 10 MB)
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-4">
                      {pendingTemplates.length > 0 && (
                        <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3">
                          <p className="text-xs text-slate-500 font-medium italic">
                            {pendingTemplates.length === 1
                              ? 'One questionnaire is still outstanding for this appointment.'
                              : `${pendingTemplates.length} questionnaires are still outstanding for this appointment.`}
                          </p>
                          <div className="flex flex-wrap gap-2">{completeOnlineButtons}</div>
                        </div>
                      )}
                      {appSubmissions.map((submission) => {
                        if (submission.scanUrl) {
                          const isFinalized = certificatesByAppointmentId.has(issuingCertApp.id);
                          const isRemoving = removingScanId === submission.id;
                          return (
                            <div
                              key={submission.id}
                              className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm"
                            >
                              <div className="bg-amber-50 px-4 py-2 border-b flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-black text-amber-700 uppercase tracking-widest">
                                    Paper Questionnaire
                                  </span>
                                  <span className="bg-amber-100 text-amber-700 text-[8px] font-black px-2 py-0.5 rounded uppercase tracking-widest border border-amber-200">
                                    Paper
                                  </span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-[9px] text-slate-400">
                                    Uploaded: {formatUkDateTime(submission.submittedAt, '—')}
                                  </span>
                                  {!isFinalized && (
                                    <button
                                      type="button"
                                      disabled={isRemoving}
                                      onClick={() => handleRemovePaperScan(submission)}
                                      className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-red-500 hover:text-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {isRemoving ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <Trash2 className="w-3 h-3" />
                                      )}
                                      Remove
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="p-5 flex flex-col gap-4">
                                <a
                                  href={submission.scanUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 text-teal-700 font-black text-[10px] uppercase tracking-widest px-4 py-2.5 rounded-xl hover:bg-teal-100 transition-all self-start"
                                >
                                  <FileText className="w-4 h-4" />
                                  View Scan
                                </a>
                                {(() => {
                                  const commentValue =
                                    localComments[submission.id]?.['paper'] ??
                                    submission.technicianComments?.['paper'] ??
                                    '';
                                  if (isFinalized) {
                                    return commentValue ? (
                                      <p className="text-xs text-slate-500 italic">
                                        <span className="font-bold text-slate-400">
                                          Technician note:
                                        </span>{' '}
                                        {commentValue}
                                      </p>
                                    ) : null;
                                  }
                                  return (
                                    <textarea
                                      className="w-full text-xs text-slate-700 border border-slate-200 rounded-lg px-2 py-1.5 resize-none placeholder:text-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-400"
                                      rows={2}
                                      placeholder="Add technician note…"
                                      defaultValue={commentValue}
                                      onBlur={(e) =>
                                        handleSaveComment(submission.id, 'paper', e.target.value)
                                      }
                                    />
                                  );
                                })()}
                              </div>
                            </div>
                          );
                        }

                        const template =
                          getTemplate(submission.templateId) ||
                          (!submission.templateId
                            ? getTemplate(service?.questionnaireConfig?.templateId)
                            : undefined);

                        if (!template) {
                          const entries = Object.entries(submission.answers || {});
                          return (
                            <div
                              key={submission.id}
                              className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm"
                            >
                              <div className="bg-slate-50 px-4 py-2 border-b flex justify-between items-center">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                  Submitted Answers
                                </span>
                                <span className="text-[9px] text-slate-400">
                                  Completed: {formatUkDateTime(submission.submittedAt, '—')}
                                </span>
                              </div>
                              <div className="divide-y divide-slate-50">
                                {entries.length === 0 ? (
                                  <div className="p-4 text-center text-xs text-slate-500">
                                    No answers recorded.
                                  </div>
                                ) : (
                                  entries.map(([key, value]) => (
                                    <div
                                      key={key}
                                      className="p-4 hover:bg-slate-50/30 transition-colors"
                                    >
                                      <p className="text-xs font-bold text-slate-500 mb-1">{key}</p>
                                      <span className="text-xs text-slate-700 font-medium">
                                        {typeof value === 'string' ? value : JSON.stringify(value)}
                                      </span>
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div
                            key={submission.id}
                            className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm"
                          >
                            <div className="bg-slate-50 px-4 py-2 border-b flex justify-between items-center">
                              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                {template.name || 'Submitted Answers'}
                              </span>
                              <span className="text-[9px] text-slate-400">
                                Completed: {formatUkDateTime(submission.submittedAt, '—')}
                              </span>
                            </div>
                            <div className="divide-y divide-slate-50">
                              {(() => {
                                const answers = (submission.answers || {}) as Record<
                                  string,
                                  unknown
                                >;
                                const unmatchedEntries = getUnmatchedAnswerEntries(
                                  answers,
                                  template.questions
                                );

                                const renderQuestionRow = (
                                  q: (typeof template.questions)[number]
                                ) => {
                                  const rawAnswer = answers[q.id];
                                  const answerText = normalizeAnswerValue(rawAnswer);
                                  const commentText = extractQuestionComment(
                                    answers,
                                    q.id,
                                    rawAnswer
                                  );
                                  const isConsentQuestion =
                                    q.id.toLowerCase().includes('consent') ||
                                    q.label.toLowerCase().includes('consent');
                                  const yesNoTone =
                                    answerText.toLowerCase() === 'yes'
                                      ? isConsentQuestion
                                        ? 'bg-green-50 text-green-600'
                                        : 'bg-red-50 text-red-600'
                                      : answerText.toLowerCase() === 'no'
                                        ? isConsentQuestion
                                          ? 'bg-red-50 text-red-600'
                                          : 'bg-green-50 text-green-600'
                                        : 'bg-slate-100 text-slate-600';
                                  return (
                                    <div
                                      key={q.id}
                                      className="p-4 hover:bg-slate-50/30 transition-colors"
                                    >
                                      <p className="text-xs font-bold text-slate-500 mb-1">
                                        {q.label}
                                      </p>
                                      <div className="flex items-center gap-2">
                                        {q.type === 'yes_no' ? (
                                          <span
                                            className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ${yesNoTone}`}
                                          >
                                            {answerText || 'No answer'}
                                          </span>
                                        ) : (
                                          <span className="text-xs text-slate-700 font-medium">
                                            {answerText || 'No answer'}
                                          </span>
                                        )}
                                      </div>
                                      {commentText && (
                                        <p className="mt-2 text-xs text-slate-700 font-medium">
                                          <span className="font-bold text-slate-500">Comment:</span>{' '}
                                          {commentText}
                                        </p>
                                      )}
                                      {(() => {
                                        const isFinalized = issuingCertApp
                                          ? certificatesByAppointmentId.has(issuingCertApp.id)
                                          : false;
                                        const commentValue =
                                          localComments[submission.id]?.[q.id] ??
                                          submission.technicianComments?.[q.id] ??
                                          '';
                                        if (isFinalized) {
                                          return commentValue ? (
                                            <p className="mt-2 text-xs text-slate-500 italic">
                                              <span className="font-bold text-slate-400">
                                                Technician note:
                                              </span>{' '}
                                              {commentValue}
                                            </p>
                                          ) : null;
                                        }
                                        return (
                                          <textarea
                                            className="mt-2 w-full text-xs text-slate-700 border border-slate-200 rounded-lg px-2 py-1.5 resize-none placeholder:text-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-400"
                                            rows={2}
                                            placeholder="Add technician note…"
                                            defaultValue={commentValue}
                                            onBlur={(e) =>
                                              handleSaveComment(submission.id, q.id, e.target.value)
                                            }
                                          />
                                        );
                                      })()}
                                    </div>
                                  );
                                };

                                // Group questions by category, preserving order
                                const hasSections = template.questions.some((q) => q.category);
                                const grouped: Array<{
                                  category: string | undefined;
                                  questions: typeof template.questions;
                                }> = [];
                                for (const q of template.questions) {
                                  const last = grouped[grouped.length - 1];
                                  if (last && last.category === q.category) {
                                    last.questions.push(q);
                                  } else {
                                    grouped.push({ category: q.category, questions: [q] });
                                  }
                                }

                                return (
                                  <>
                                    {hasSections
                                      ? grouped.map((group, gi) => (
                                          <div key={gi}>
                                            {group.category && (
                                              <div className="px-4 py-2 bg-slate-100 border-y border-slate-200">
                                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                                                  {group.category}
                                                </span>
                                              </div>
                                            )}
                                            {group.questions.map(renderQuestionRow)}
                                          </div>
                                        ))
                                      : template.questions.map(renderQuestionRow)}
                                    {unmatchedEntries.length > 0 && (
                                      <div className="p-4 bg-amber-50/40 border-t border-amber-100">
                                        <p className="text-[10px] font-black uppercase tracking-widest text-amber-700">
                                          Additional Response Data
                                        </p>
                                        <div className="mt-2 space-y-1">
                                          {unmatchedEntries.map(([key, value]) => (
                                            <p
                                              key={`${submission.id}-${key}`}
                                              className="text-xs text-slate-700"
                                            >
                                              <span className="font-bold text-slate-500">
                                                {key}:
                                              </span>{' '}
                                              {normalizeAnswerValue(value) || 'No answer'}
                                            </p>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Attached documents (audiograms, referrals, reports) */}
              <div className="mb-8">
                <AppointmentDocumentsPanel appointmentId={issuingCertApp.id} />
              </div>

              {/* Technician Notes Summary */}
              {(() => {
                if (!issuingCertApp) return null;
                const appSubmissions = getSubmissions(issuingCertApp.id);
                const notedEntries: Array<{ questionLabel: string; answer: string; note: string }> =
                  [];
                appSubmissions.forEach((submission) => {
                  const template = getTemplate(submission.templateId);
                  if (!template) return;
                  const comments =
                    localComments[submission.id] ?? submission.technicianComments ?? {};
                  template.questions.forEach((q) => {
                    const note = comments[q.id];
                    if (!note || !note.trim()) return;
                    const rawAnswer = (submission.answers || {})[q.id];
                    const answerText = normalizeAnswerValue(rawAnswer as unknown);
                    notedEntries.push({
                      questionLabel: q.label,
                      answer: answerText || 'No answer',
                      note: note.trim()
                    });
                  });
                });
                if (notedEntries.length === 0) return null;
                return (
                  <div className="border-t border-slate-100 pt-8">
                    <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-4">
                      Technician Notes Summary
                    </h4>
                    <div className="space-y-3">
                      {notedEntries.map((entry, i) => (
                        <div key={i} className="bg-amber-50 border border-amber-100 rounded-xl p-3">
                          <p className="text-xs font-bold text-slate-600">{entry.questionLabel}</p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            <span className="font-semibold">A:</span> {entry.answer}
                          </p>
                          <p className="text-xs text-slate-700 mt-1">
                            <span className="font-semibold text-amber-700">Note:</span> {entry.note}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Outcome Section */}
              <div className="border-t border-slate-100 pt-8">
                <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                  <Award className="w-4 h-4 text-teal-600" /> Clinical Conclusion
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {CERTIFICATE_OUTCOMES.map((opt) => (
                    <label
                      key={opt.id}
                      className={`border-2 rounded-2xl p-4 cursor-pointer transition-all flex flex-col items-center justify-center text-center gap-1.5 ${certData.outcome === opt.id ? `${opt.bg} ${opt.border} ring-4 ring-slate-900/5` : `border-slate-100 bg-white ${opt.hover}`}`}
                    >
                      <input
                        type="radio"
                        className="hidden"
                        checked={certData.outcome === opt.id}
                        onChange={() => setCertData({ ...certData, outcome: opt.id })}
                      />
                      <span
                        className={`font-black text-xs uppercase tracking-widest ${certData.outcome === opt.id ? opt.text : 'text-slate-500'}`}
                      >
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {getExtraResultItems(issuingCertApp).length > 0 && (
                <div className="border-t border-slate-100 pt-8">
                  <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                    <ClipboardCheck className="w-4 h-4 text-teal-600" /> Individual Test Results
                  </h4>
                  <p className="text-[10px] text-slate-500 mb-3">
                    A result per additional service/test — each prints as its own row on the
                    certificate. The clinical conclusion above covers{' '}
                    {getPrimaryServiceName(issuingCertApp)}. Drug &amp; Alcohol results come from
                    the Drug Test Result picker below.
                  </p>
                  <div className="space-y-2">
                    {getExtraResultItems(issuingCertApp).map((item) => (
                      <div
                        key={item.key}
                        className="flex items-center justify-between gap-3 border-2 border-slate-100 rounded-2xl p-3 bg-slate-50"
                      >
                        <span className="text-xs font-bold text-slate-700">{item.name}</span>
                        <select
                          className="border-2 border-slate-100 rounded-xl p-2 text-xs font-bold bg-white outline-none focus:border-teal-400"
                          value={extraResults[item.key] || 'fit'}
                          onChange={(e) =>
                            setExtraResults((prev) => ({
                              ...prev,
                              [item.key]: e.target.value as CertificateResultOutcome
                            }))
                          }
                        >
                          <option value="fit">Fit</option>
                          <option value="fit_restrictions">Fit with Restriction</option>
                          <option value="referred">Referred to OHP</option>
                          <option value="unfit">Unfit</option>
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-slate-100 pt-8">
                <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                  <TestTube className="w-4 h-4 text-teal-600" /> Drug Test Result
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { id: '', label: 'Not Recorded' },
                    { id: 'negative', label: 'Negative' },
                    { id: 'non_negative', label: 'Non-negative' },
                    { id: 'not_tested', label: 'Not Tested' }
                  ].map((option) => {
                    const isActive = String(certData.drugTestOutcome || '') === option.id;
                    return (
                      <label
                        key={option.id || 'empty'}
                        className={`border rounded-2xl p-3 cursor-pointer transition-all flex items-center justify-center text-center ${isActive ? 'bg-teal-50 border-teal-500 ring-2 ring-teal-100' : 'border-slate-200 bg-white hover:border-teal-300'}`}
                      >
                        <input
                          type="radio"
                          className="hidden"
                          checked={isActive}
                          onChange={() =>
                            setCertData({
                              ...certData,
                              drugTestOutcome:
                                (option.id as MedicalCertificate['drugTestOutcome']) || undefined
                            })
                          }
                        />
                        <span
                          className={`font-black text-[10px] uppercase tracking-widest ${isActive ? 'text-teal-700' : 'text-slate-500'}`}
                        >
                          {option.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <p className="mt-2 text-[10px] text-slate-500">
                  Optional. Leave as "Not Recorded" to store no drug test result.
                </p>
              </div>

              {(() => {
                const assessment = getHealthAssessment(issuingCertApp);
                const selectedClauses =
                  assessment?.fitStatus === 'fit_restrictions'
                    ? (assessment.restrictions || []).map((r) => r.trim()).filter(Boolean)
                    : [];
                // Restrictions are printed only alongside a "Fit with Restriction"
                // determination, so a Fit/Referred/Unfit conclusion hides the whole
                // section rather than collecting text the certificate will drop.
                const willPrintRestrictions = certificateHasRestrictionOutcome({
                  outcome: certData.outcome || 'fit',
                  results: buildCertificateResults(issuingCertApp)
                });
                const hasSelectedClauses = willPrintRestrictions && selectedClauses.length > 0;
                return (
                  <div>
                    <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-teal-600" /> Restrictions
                    </h4>
                    {!willPrintRestrictions && (
                      <p className="border-2 border-slate-100 bg-slate-50 rounded-2xl p-4 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                        The conclusion above is not "Fit with Restriction" — no restrictions are
                        printed on this certificate.
                      </p>
                    )}
                    {hasSelectedClauses && (
                      <div className="mb-4 border-2 border-yellow-200 bg-yellow-50 rounded-2xl p-4">
                        <p className="text-[10px] font-black uppercase tracking-widest text-yellow-700 mb-2">
                          From Health Assessment — these will appear on the certificate
                        </p>
                        <ul className="list-disc list-inside space-y-1">
                          {selectedClauses.map((clause) => (
                            <li key={clause} className="text-xs font-medium text-slate-700">
                              {clause}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {hasSelectedClauses && (
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                        Additional restrictions (optional)
                      </p>
                    )}
                    {willPrintRestrictions && (
                      <textarea
                        className="w-full border-2 border-slate-100 rounded-2xl p-4 text-sm h-24 focus:ring-4 focus:ring-teal-500/10 focus:border-teal-400 outline-none transition-all bg-slate-50 font-medium"
                        placeholder="Specific work restrictions for employer (e.g. no heavy lifting, avoid heights)..."
                        value={certData.restrictions || ''}
                        onChange={(e) => setCertData({ ...certData, restrictions: e.target.value })}
                      />
                    )}
                  </div>
                );
              })()}

              <div>
                <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-teal-600" /> Clinical Advice
                </h4>
                <textarea
                  className="w-full border-2 border-slate-100 rounded-2xl p-4 text-sm h-32 focus:ring-4 focus:ring-teal-500/10 focus:border-teal-400 outline-none transition-all bg-slate-50 font-medium"
                  placeholder="General clinical notes for employer and patient..."
                  value={certData.advice}
                  onChange={(e) => setCertData({ ...certData, advice: e.target.value })}
                />
              </div>

              <div>
                <h4 className="font-black text-slate-400 text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                  <PenTool className="w-4 h-4 text-teal-600" /> Signing Technician
                </h4>
                <select
                  className="w-full border-2 border-slate-100 rounded-2xl p-4 text-sm focus:ring-4 focus:ring-teal-500/10 focus:border-teal-400 outline-none transition-all bg-slate-50 font-medium"
                  value={selectedSignerId}
                  onChange={(e) => setSelectedSignerId(e.target.value)}
                >
                  {isAmendingMode && (
                    <option value="">
                      Keep current —{' '}
                      {certificatesByAppointmentId.get(issuingCertApp.id)?.technicianName ||
                        'unknown'}
                    </option>
                  )}
                  {!signers.some((s) => s.id === currentUser.id) && (
                    <option value={currentUser.id}>{currentUser.name} (me)</option>
                  )}
                  {signers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                      {s.id === currentUser.id ? ' (me)' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-400 mt-2 font-medium">
                  The selected technician's name and saved signature are printed at the bottom of
                  the certificate. Only technicians with a saved signature are listed.
                </p>
              </div>
            </div>

            <div className="p-6 border-t bg-slate-50 flex flex-col sm:flex-row justify-end gap-4">
              <div className="flex gap-2">
                <button
                  onClick={closeRecordModal}
                  disabled={isIssuing}
                  className="px-6 py-3 text-slate-400 font-bold uppercase text-[10px] tracking-widest"
                >
                  Discard
                </button>
                <button
                  onClick={() => setIsHealthAssessmentModalOpen(true)}
                  className="bg-white text-teal-600 border-2 border-teal-200 px-6 py-3 rounded-2xl font-black hover:bg-teal-50 flex items-center gap-2 shadow-sm transition-all uppercase tracking-widest text-[11px]"
                >
                  {issuingCertApp && getHealthAssessment(issuingCertApp)
                    ? 'Edit Health Assessment'
                    : 'Enter Technician Entry'}
                </button>
                {issuingCertApp && getHealthAssessment(issuingCertApp) && (
                  <button
                    onClick={handleSubmitCertificate}
                    disabled={isIssuing}
                    className="bg-teal-600 text-white px-8 py-3 rounded-2xl font-black hover:bg-teal-700 flex items-center gap-2 disabled:opacity-50 shadow-xl shadow-teal-600/20 active:scale-95 transition-all uppercase tracking-widest text-[11px]"
                  >
                    {isIssuing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Award className="w-4 h-4" />
                    )}{' '}
                    {isAmendingMode ? 'Save Amendment' : 'Generate Certificate'}
                  </button>
                )}
              </div>
            </div>
          </div>
          {isHealthAssessmentModalOpen && issuingCertApp && (
            <HealthAssessmentModal
              appointment={issuingCertApp}
              initialData={getHealthAssessment(issuingCertApp)}
              drugTestOutcome={certData.drugTestOutcome}
              defaultAssessmentType={(() => {
                // Screening-only bookings get the lighter screening flow; ANY
                // Medical/Vaccination service in the set requires the full assessment.
                const categories = getAppointmentServiceIds(issuingCertApp)
                  .map((id) => servicesById.get(id)?.category)
                  .filter(Boolean);
                return categories.length > 0 && categories.every((c) => c === 'Screening')
                  ? 'screening'
                  : 'full';
              })()}
              onSaved={(ha) => {
                setLocalHealthAssessments((prev) => ({ ...prev, [issuingCertApp.id]: ha }));
                setIsHealthAssessmentModalOpen(false);
              }}
              onClose={() => setIsHealthAssessmentModalOpen(false)}
            />
          )}
          {fillingQuestionnaireTemplateId &&
            issuingCertApp &&
            (() => {
              const template = questionnairesById.get(fillingQuestionnaireTemplateId);
              if (!template) return null;
              return (
                <TechnicianQuestionnaireModal
                  appointment={issuingCertApp}
                  template={template}
                  onSubmitted={(submission) => {
                    setLocalSubmissions((prev) => [...prev, submission]);
                    setFillingQuestionnaireTemplateId(null);
                  }}
                  onClose={() => setFillingQuestionnaireTemplateId(null)}
                />
              );
            })()}
        </div>
      )}

      {issuedCertificate && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center">
            <div className="bg-green-50 border-2 border-green-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CheckSquare className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="font-black text-xl text-slate-900 mb-1">Certificate Issued</h3>
            <p className="text-sm text-slate-500 font-medium mb-6">
              Certificate for {issuedCertificate.appointment.customerName} has been issued. Print a
              copy for the candidate to take with them.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <button
                onClick={() =>
                  handlePrintCertificate(
                    issuedCertificate.appointment,
                    issuedCertificate.certificate
                  )
                }
                className="bg-teal-600 text-white px-8 py-3 rounded-2xl font-black hover:bg-teal-700 flex items-center justify-center gap-2 shadow-xl shadow-teal-600/20 active:scale-95 transition-all uppercase tracking-widest text-[11px]"
              >
                <Printer className="w-4 h-4" /> Print Certificate
              </button>
              <button
                onClick={() => setIssuedCertificate(null)}
                className="px-6 py-3 text-slate-400 font-bold uppercase text-[10px] tracking-widest"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main UI Header */}
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
        <div>
          <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">
            Clinician Dashboard
          </h2>
          <p className="text-slate-500 text-sm font-medium">
            Logged in as <span className="text-teal-600 font-black">{currentUser.name}</span>
          </p>
        </div>
        <button
          onClick={() => setIsSignatureModalOpen(true)}
          className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-teal-600 bg-teal-50 hover:bg-teal-100 px-4 py-2.5 rounded-xl transition-all border border-teal-100"
        >
          {currentUser.signature ? 'Update Signature' : 'Setup Signature'}
        </button>
      </div>

      {/* Site Filters */}
      <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm flex flex-col gap-3 mb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[2fr_1fr_1fr_1fr] gap-3">
          <div className="flex bg-slate-50 p-1 rounded-xl w-full min-w-[260px]">
            <button
              onClick={() => setDateRangeFilter('past')}
              className={`flex-1 py-2 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all whitespace-nowrap ${dateRangeFilter === 'past' ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
            >
              Past
            </button>
            <button
              onClick={() => setDateRangeFilter('today')}
              className={`flex-1 py-2 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all whitespace-nowrap ${dateRangeFilter === 'today' ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
            >
              Today
            </button>
            <button
              onClick={() => setDateRangeFilter('tomorrow')}
              className={`flex-1 py-2 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all whitespace-nowrap ${dateRangeFilter === 'tomorrow' ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
            >
              Tomorrow
            </button>
            <button
              onClick={() => setDateRangeFilter('week')}
              className={`flex-1 py-2 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all whitespace-nowrap ${dateRangeFilter === 'week' ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
            >
              Full Rota
            </button>
          </div>
          <select
            className="bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-600 px-4 py-2.5 rounded-xl border-none outline-none focus:ring-0 cursor-pointer w-full"
            value={selectedVenueFilter}
            onChange={(e) => setSelectedVenueFilter(e.target.value)}
          >
            <option value="all">All Authorized Sites</option>
            {((venues as Venue[]) || [])
              .filter((v: Venue) => {
                const assigned = currentUser.assignedVenueIds;
                return !assigned || assigned.length === 0 || assigned.includes(v.id);
              })
              .map((v: Venue) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
          </select>
          <select
            className="bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-600 px-4 py-2.5 rounded-xl border-none outline-none focus:ring-0 cursor-pointer w-full"
            value={statusFilter}
            onChange={(e) => {
              if (isStatusFilter(e.target.value)) {
                setStatusFilter(e.target.value);
              }
            }}
          >
            <option value="all">All Statuses</option>
            <option value="booked">Booked</option>
            <option value="completed">Completed</option>
            <option value="noshow">No Show</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            className="bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-600 px-4 py-2.5 rounded-xl border-none outline-none focus:ring-0 cursor-pointer w-full"
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
          >
            <option value="all">All Services</option>
            {[...services]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_200px] gap-3">
          <input
            className="flex-1 bg-slate-50 text-xs font-semibold text-slate-700 placeholder:text-slate-400 px-4 py-2.5 rounded-xl border border-slate-100 outline-none focus:ring-2 focus:ring-teal-200"
            placeholder="Search name, email, or reference..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            aria-label="Items per page"
            className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 shadow-sm outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>
        </div>
      </div>

      <DataErrorBoundary invalidateOnRetry="appointments">
        <Suspense
          fallback={
            <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-slate-200 shadow-sm flex flex-col items-center">
              <div className="bg-slate-50 p-4 rounded-full mb-4">
                <Loader2 className="w-10 h-10 text-slate-200 animate-spin" />
              </div>
              <p className="text-slate-800 font-black uppercase tracking-widest text-sm">
                Loading appointments...
              </p>
            </div>
          }
        >
          <TechnicianBookingsPanel
            page={page}
            pageSize={pageSize}
            statusFilter={statusFilter}
            serviceFilter={serviceFilter}
            venueIdParam={venueIdParam}
            dateFrom={dateFrom}
            dateTo={dateTo}
            sortDir={dateRangeFilter === 'past' ? 'desc' : 'asc'}
            search={deferredSearchTerm}
            servicesById={servicesById}
            addOnsById={addOnsById}
            venuesById={venuesById}
            submissions={submissions}
            certificatesByAppointmentId={certificatesByAppointmentId}
            onItemsLoaded={(items, total) => {
              setPageItems(items);
              setPageTotalCount(total);
            }}
            onMarkNoShow={(id) => setNoShowConfirmId(id)}
            onOpenRecord={openCertificateRecord}
            onAmendRecord={openAmendRecord}
            onPrintRecord={handlePrintCertificate}
          />
        </Suspense>
      </DataErrorBoundary>

      {pageItems.length > 0 && (
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-xs text-slate-500 font-semibold">
            Showing {pageRangeStart}-{pageRangeEnd} of {totalFiltered}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* No Show Modal */}
      {noShowConfirmId && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-sm w-full text-center animate-scale-up">
            <div className="bg-red-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <UserX className="w-10 h-10 text-red-600" />
            </div>
            <h3 className="font-black text-xl text-slate-900 uppercase tracking-tight mb-2">
              Mark as Missed?
            </h3>
            <p className="text-slate-500 text-xs mb-8 leading-relaxed font-medium">
              This will close the record as a 'No Show'. Automated alerts will be sent to the
              company.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setNoShowConfirmId(null)}
                className="py-3 px-6 text-slate-400 font-black uppercase text-[10px] tracking-widest"
              >
                Keep Open
              </button>
              <button
                onClick={() => {
                  appointmentMutations
                    .updateStatus(noShowConfirmId, 'noshow')
                    .catch((err: unknown) =>
                      showAlertModal(normalizeErrorMessage(err, 'Failed to update status.'))
                    );
                  setNoShowConfirmId(null);
                }}
                className="py-3 px-6 bg-red-600 text-white font-black uppercase text-[10px] tracking-widest rounded-2xl shadow-lg shadow-red-600/20"
              >
                Confirm No-Show
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface TechnicianBookingsPanelProps {
  page: number;
  pageSize: number;
  statusFilter: 'all' | 'booked' | 'completed' | 'noshow' | 'cancelled';
  serviceFilter: string;
  venueIdParam?: string;
  dateFrom?: string;
  dateTo: string;
  sortDir: 'asc' | 'desc';
  search: string;
  servicesById: Map<string, Service>;
  addOnsById: Map<string, AddOn>;
  venuesById: Map<string, Venue>;
  submissions: QuestionnaireSubmission[];
  certificatesByAppointmentId: Map<string, MedicalCertificate>;
  onItemsLoaded: (items: Appointment[], totalCount: number) => void;
  onMarkNoShow: (id: string) => void;
  onOpenRecord: (app: Appointment) => void;
  onAmendRecord: (app: Appointment) => void;
  onPrintRecord: (app: Appointment) => void;
}

const TechnicianBookingsPanel: React.FC<TechnicianBookingsPanelProps> = ({
  page,
  pageSize,
  statusFilter,
  serviceFilter,
  venueIdParam,
  dateFrom,
  dateTo,
  sortDir,
  search,
  servicesById,
  addOnsById,
  venuesById,
  submissions,
  certificatesByAppointmentId,
  onItemsLoaded,
  onMarkNoShow,
  onOpenRecord,
  onAmendRecord,
  onPrintRecord
}) => {
  const params: AppointmentListParams = {
    page,
    pageSize,
    status: statusFilter === 'all' ? undefined : statusFilter,
    venueId: venueIdParam,
    serviceId: serviceFilter === 'all' ? undefined : serviceFilter,
    dateFrom,
    dateTo,
    q: search.trim() || undefined,
    sortBy: 'date',
    sortDir
  };
  const result = useAppointments(params);
  const items = result.items || [];
  const totalCount = Number(result.totalCount || 0);

  useEffect(() => {
    onItemsLoaded(items, totalCount);
  }, [items, totalCount, onItemsLoaded]);

  if (items.length === 0) {
    return (
      <div className="space-y-12">
        <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-slate-200 shadow-sm flex flex-col items-center">
          <div className="bg-slate-50 p-4 rounded-full mb-4">
            <Calendar className="w-10 h-10 text-slate-200" />
          </div>
          <p className="text-slate-800 font-black uppercase tracking-widest text-sm">
            No appointments match your filters
          </p>
          <p className="text-slate-400 text-xs mt-2 max-w-[250px] mx-auto leading-relaxed">
            Adjust the date range or clear filters to see more appointments.
          </p>
        </div>
      </div>
    );
  }

  const grouped = items
    .slice()
    .sort((a, b) => a.time.localeCompare(b.time))
    .reduce(
      (acc, a) => {
        const k = new Date(a.date).toDateString();
        if (!acc[k]) acc[k] = [];
        acc[k].push(a);
        return acc;
      },
      {} as Record<string, Appointment[]>
    );

  return (
    <div className="space-y-12">
      {(Object.entries(grouped) as [string, Appointment[]][]).map(([date, apps]) => (
        <div key={date} className="space-y-6">
          <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] flex items-center gap-3 px-2">
            <div className="h-px bg-slate-100 flex-1"></div>
            {date}
            <div className="h-px bg-slate-100 flex-1"></div>
          </h3>
          <div className="grid gap-4 xl:grid-cols-2">
            {apps.map((app) => {
              const svcNames = getAppointmentServiceNames(app, servicesById);
              const appAddOns = (app.addOnIds || [])
                .map((id) => addOnsById.get(id))
                .filter((a): a is AddOn => Boolean(a));
              const isDone = app.status === 'completed';
              const isNoShow = app.status === 'noshow';
              const isCancelled = app.status === 'cancelled';
              const submissionCount = submissions.filter((s) => s.appointmentId === app.id).length;
              const hasSubmission = submissionCount > 0;

              return (
                <div
                  key={app.id}
                  className={`bg-white rounded-3xl border p-6 flex flex-col sm:flex-row justify-between items-center gap-6 shadow-sm transition-all ${isNoShow || isCancelled ? 'opacity-50 border-slate-50' : isDone ? 'border-slate-100' : 'border-slate-200 hover:border-teal-200 hover:shadow-md'}`}
                >
                  <div className={`flex items-center gap-6 flex-1 ${isDone ? 'opacity-60' : ''}`}>
                    <div className="bg-slate-50 border-2 border-slate-100 w-20 h-20 rounded-2xl flex items-center justify-center shrink-0 shadow-inner">
                      <Clock className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-black text-xl text-slate-900 leading-tight">
                          {app.customerName}
                        </h3>
                        {hasSubmission && (
                          <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-wider border border-blue-100">
                            {submissionCount > 1 ? `${submissionCount} Forms` : 'Form Ready'}
                          </span>
                        )}
                        {app.staffPrivateNote && (
                          <span
                            className="bg-amber-50 text-amber-600 px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-wider border border-amber-100"
                            title="Notes Available"
                          >
                            Notes
                          </span>
                        )}
                      </div>
                      <div className="text-teal-700 font-bold text-xs uppercase tracking-widest">
                        {svcNames}
                      </div>
                      {appAddOns.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                          {appAddOns.map((addOn) => (
                            <span
                              key={addOn.id}
                              className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider border border-purple-100"
                            >
                              + {addOn.name}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-4 mt-3">
                        <span className="text-[9px] font-black uppercase text-slate-300 tracking-widest">
                          {app.time}
                        </span>
                        <span className="text-[9px] font-black uppercase text-slate-300 tracking-widest flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {venuesById.get(app.venueId)?.city}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {isDone ? (
                      certificatesByAppointmentId.has(app.id) ? (
                        <>
                          <span className="flex items-center gap-2 text-green-600 font-black text-[10px] px-6 py-3 bg-green-50 rounded-2xl border border-green-100 uppercase tracking-widest">
                            <CheckSquare className="w-4 h-4" /> Certified
                          </span>
                          {canPrintCertificate(certificatesByAppointmentId.get(app.id)) && (
                            <button
                              onClick={() => onPrintRecord(app)}
                              className="text-[10px] font-black uppercase tracking-widest bg-teal-600 text-white border-2 border-teal-600 px-4 py-3 rounded-2xl hover:bg-teal-700 hover:border-teal-700 transition-all flex items-center gap-2 shadow-sm"
                              title="Print certificate for the candidate"
                            >
                              <Printer className="w-4 h-4" /> Print
                            </button>
                          )}
                          <button
                            onClick={() => onAmendRecord(app)}
                            className="text-[10px] font-black uppercase tracking-widest bg-white text-slate-700 border-2 border-slate-300 px-4 py-3 rounded-2xl hover:bg-slate-50 hover:border-slate-400 transition-all"
                            title="Correct a mistake on this certificate"
                          >
                            Amend
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="flex items-center gap-2 text-amber-600 font-black text-[10px] px-6 py-3 bg-amber-50 rounded-2xl border border-amber-100 uppercase tracking-widest">
                            <Clock className="w-4 h-4" /> Cert Pending
                          </span>
                          <button
                            onClick={() => onOpenRecord(app)}
                            className="bg-slate-900 text-white px-8 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/10 active:scale-95"
                            title="Record results and issue the certificate"
                          >
                            Issue Certificate
                          </button>
                        </>
                      )
                    ) : isNoShow ? (
                      <span className="flex items-center gap-2 text-red-600 font-black text-[10px] px-6 py-3 bg-red-50 rounded-2xl border border-red-100 uppercase tracking-widest">
                        <UserX className="w-4 h-4" /> Missed
                      </span>
                    ) : isCancelled ? (
                      <span className="flex items-center gap-2 text-slate-600 font-black text-[10px] px-6 py-3 bg-slate-50 rounded-2xl border border-slate-100 uppercase tracking-widest">
                        <UserX className="w-4 h-4" /> Cancelled
                      </span>
                    ) : (
                      <>
                        <button
                          onClick={() => onMarkNoShow(app.id)}
                          className="p-3 text-red-300 hover:text-red-500 transition-colors"
                        >
                          <UserX className="w-6 h-6" />
                        </button>
                        <button
                          onClick={() => onOpenRecord(app)}
                          className="bg-slate-900 text-white px-8 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/10 active:scale-95"
                        >
                          Open Record
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
